import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { FilterSidebar, type FilterGroup } from "@/components/filter-sidebar";
import { ListToolbar } from "@/components/list-toolbar";
import { PaginationBar } from "@/components/pagination-bar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useList } from "@/hooks/use-list";
import { api } from "@/lib/api";
import { Plus, Trash2, ExternalLink, Linkedin, Phone, MoreHorizontal, Upload } from "lucide-react";
import { cn } from "@/lib/cn";

type Filter =
  | "all" | "clients" | "prospects" | "partners" | "government"
  | "us" | "india" | "glocalize" | "hot" | "proposalSent" | "recent30";

export default function AccountsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [filter, setFilter] = useState<Filter>("all");
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const users = useQuery({
    queryKey: ["/users"],
    queryFn: async () => (await api.get("/users")).data.data as { id: string; name: string }[],
  });

  const extraParams = useMemo(() => {
    const p: Record<string, unknown> = {};
    if (filter === "clients")    p.type = "CLIENT";
    if (filter === "prospects")  p.type = "PROSPECT";
    if (filter === "partners")   p.type = "PARTNER";
    if (filter === "government") p.type = "GOVERNMENT";
    if (filter === "us")         p.entity = "US";
    if (filter === "india")      p.entity = "INDIA";
    if (filter === "glocalize")  p.entity = "GLOCALIZE";
    if (ownerId)                 p.ownerId = ownerId;
    return p;
  }, [filter, ownerId]);

  const { data, isLoading } = useList<any>("/accounts", {
    q, page, pageSize, sortBy, sortDir, ...extraParams,
  });

  const rows = useMemo(() => {
    const all = data?.data ?? [];
    const since30 = Date.now() - 30 * 86400_000;
    if (filter === "hot")          return all.filter((a: any) => a.rating === "HOT");
    if (filter === "proposalSent") return all.filter((a: any) => a.proposalSent);
    if (filter === "recent30")     return all.filter((a: any) => new Date(a.createdAt).getTime() >= since30);
    return all;
  }, [data, filter]);

  const total = data?.pagination?.total;

  const groups: FilterGroup[] = [
    {
      title: "Standard",
      items: [
        { label: "All accounts", active: filter === "all",        onClick: () => setFilter("all"), count: total },
        { label: "Clients",      active: filter === "clients",    onClick: () => setFilter("clients") },
        { label: "Prospects",    active: filter === "prospects",  onClick: () => setFilter("prospects") },
        { label: "Partners",     active: filter === "partners",   onClick: () => setFilter("partners") },
        { label: "Government",   active: filter === "government", onClick: () => setFilter("government") },
      ],
    },
    {
      title: "Entity",
      items: [
        { label: "US",           active: filter === "us",         onClick: () => setFilter("us") },
        { label: "India",        active: filter === "india",      onClick: () => setFilter("india") },
        { label: "Glocalize",    active: filter === "glocalize",  onClick: () => setFilter("glocalize") },
      ],
    },
    {
      title: "Engagement",
      items: [
        { label: "Hot rating",      active: filter === "hot",           onClick: () => setFilter("hot") },
        { label: "Proposal sent",   active: filter === "proposalSent",  onClick: () => setFilter("proposalSent") },
        { label: "Created last 30d", active: filter === "recent30",     onClick: () => setFilter("recent30") },
      ],
    },
    {
      title: "Owner",
      items: [
        { label: "All owners", active: !ownerId, onClick: () => setOwnerId(null) },
        ...(users.data ?? []).map((u) => ({ label: u.name, active: ownerId === u.id, onClick: () => setOwnerId(u.id) })),
      ],
    },
  ];

  async function del(id: string, name: string) {
    if (!confirm(`Delete account "${name}"? This cannot be undone.`)) return;
    await api.delete(`/accounts/${id}`);
    qc.invalidateQueries({ queryKey: ["/accounts"] });
  }

  function currency(v: any) {
    if (v === null || v === undefined || v === "") return "—";
    const n = Number(v);
    if (Number.isNaN(n)) return "—";
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
    return `$${n.toLocaleString()}`;
  }

  return (
    <>
      <PageHeader
        title="Accounts"
        description="Clients, prospects, partners, government agencies."
        actions={
          <>
            <Button asChild variant="outline"><Link to="/accounts/import"><Upload className="mr-1 h-4 w-4"/>Import CSV</Link></Button>
            <Button asChild><Link to="/accounts/new"><Plus className="mr-1 h-4 w-4"/>New Account</Link></Button>
          </>
        }
      />

      <ListToolbar
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        sortBy={sortBy}
        sortDir={sortDir}
        sortOptions={[
          { value: "createdAt",   label: "Created date" },
          { value: "updatedAt",   label: "Last modified" },
          { value: "name",        label: "Name" },
          { value: "healthScore", label: "Health score" },
        ]}
        onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
        rightSlot={
          <Input placeholder="Search records…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="h-8 w-64" />
        }
      />

      <div className="flex flex-1 min-h-[60vh]">
        {filtersOpen && <FilterSidebar groups={groups} onClear={() => setFilter("all")} />}

        <div className="flex flex-1 flex-col min-w-0">
          <div className="flex-1 overflow-auto">
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-10"><input type="checkbox" disabled /></Th>
                  <Th>Account Name</Th>
                  <Th>Type</Th>
                  <Th>Entity</Th>
                  <Th>Industry</Th>
                  <Th>Rating</Th>
                  <Th className="text-right">Revenue</Th>
                  <Th>Website</Th>
                  <Th>Owner</Th>
                  <Th className="text-right">Health</Th>
                  <Th className="w-10"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading && <Tr><Td colSpan={11} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
                {!isLoading && rows.length === 0 && (
                  <Tr><Td colSpan={11} className="py-12 text-center text-muted-foreground">
                    No accounts in this view.
                  </Td></Tr>
                )}
                {rows.map((a: any) => (
                  <Tr key={a.id} className="group">
                    <Td><input type="checkbox" disabled /></Td>
                    <Td>
                      <Link to={`/accounts/${a.id}`} className="font-medium hover:underline">{a.name}</Link>
                      {a.proposalSent && <Badge variant="secondary" className="ml-2">Proposal sent</Badge>}
                    </Td>
                    <Td><Badge variant="secondary">{a.type}</Badge></Td>
                    <Td>{a.entity}</Td>
                    <Td className="text-muted-foreground">{a.industry ?? "—"}</Td>
                    <Td>{a.rating ? (
                      <Badge variant={a.rating === "HOT" ? "destructive" : a.rating === "WARM" ? "default" : "secondary"}>{a.rating}</Badge>
                    ) : <span className="text-muted-foreground">—</span>}</Td>
                    <Td className="text-right tabular-nums">{currency(a.annualRevenue)}</Td>
                    <Td>{a.website ? (
                      <a href={a.website} target="_blank" rel="noreferrer" className="text-muted-foreground hover:underline">
                        {a.domain ?? "Open"}
                      </a>
                    ) : <span className="text-muted-foreground">—</span>}</Td>
                    <Td className="text-muted-foreground">{a.owner?.name ?? "—"}</Td>
                    <Td className="text-right tabular-nums">{a.healthScore ?? "—"}</Td>
                    <Td>
                      <div className={cn("flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity")}>
                        {a.website && (
                          <a href={a.website} target="_blank" rel="noreferrer" title="Website" className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                            <ExternalLink className="h-3.5 w-3.5"/>
                          </a>
                        )}
                        {a.linkedinUrl && (
                          <a href={a.linkedinUrl} target="_blank" rel="noreferrer" title="LinkedIn" className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                            <Linkedin className="h-3.5 w-3.5"/>
                          </a>
                        )}
                        {a.phone && (
                          <a href={`tel:${a.phone}`} title="Call" className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                            <Phone className="h-3.5 w-3.5"/>
                          </a>
                        )}
                        <button onClick={() => del(a.id, a.name)} title="Delete" className="flex h-7 w-7 items-center justify-center rounded-md text-destructive hover:bg-muted">
                          <Trash2 className="h-3.5 w-3.5"/>
                        </button>
                        <Link to={`/accounts/${a.id}`} title="Open" className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                          <MoreHorizontal className="h-3.5 w-3.5"/>
                        </Link>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>

          <PaginationBar
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          />
        </div>
      </div>
    </>
  );
}
